// src/utils/energyApi.js
import { API_BASE } from './api';
import { mockEnergySummaryResponse } from '../__fixtures__/testData';

const USE_FIXTURE_ENERGY_DATA = process.env.REACT_APP_USE_ENERGY_FIXTURE === 'true';

/**
 * Transforms the getEnergySummary contract into per-day rows the UI consumes.
 */
export function transformEnergySummary(data) {
  if (!data || !Array.isArray(data.summarys)) {
    return [];
  }
  return data.summarys
    .filter(day => day != null)
    .map(day => {
      const sessions = Array.isArray(day.sessions) ? day.sessions.filter(Boolean) : [];
      const totalPowerWh = sessions.reduce((sum, s) => sum + (typeof s.power_provided === 'number' ? s.power_provided : 0), 0);
      
      let date = new Date(day.sst);
      if (isNaN(date.getTime())) {
        date = new Date();
      }

      const backupTimeHours = typeof day.dur === 'number' ? day.dur / 3600 : 0;

      return {
        date,
        totalPowerWh,
        backupTimeHours,
        sessions
      };
    });
}

/**
 * Fetches energy summary for a device across a date range. Backed by a
 * getEnergySummary endpoint to be delivered separately (see spec);
 * REACT_APP_USE_ENERGY_FIXTURE=true serves fixture data instead so the UI
 * can be built/tested before that endpoint ships.
 */
export async function fetchEnergySummary(authData, vehicleDbId, startDate, endDate) {
  if (USE_FIXTURE_ENERGY_DATA) {
    return transformEnergySummary(mockEnergySummaryResponse);
  }

  const url = `${API_BASE}/api/getSummarys?_vehicle=${vehicleDbId}&startTime=${startDate.getTime()}&endTime=${endDate.getTime()}&api_access_token=${authData.token}`;
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  const data = await response.json();
  if (data.status !== 'success') {
    throw new Error(data.message || 'Failed to load energy summary');
  }
  return transformEnergySummary(data);
}

/**
 * Fetches the latest cumulative summary for a vehicle from the getLatestSummary endpoint.
 */
export async function fetchLatestSummary(authData, vehicleDbId) {
  if (USE_FIXTURE_ENERGY_DATA) {
    return {
      veh: vehicleDbId,
      cum_fc: 7.3, // 7.3 kWh total
      cum_wrk: 4, // 4 hours total
      sessions: [
        {
          battery_name: 'Battery 1 (Slave 3)',
          start_time: '2026-06-30T02:00:00.000Z',
          end_time: '2026-06-30T04:00:00.000Z',
          power_provided: 3000,
          backup_time: 7200
        },
        {
          battery_name: 'Battery 2 (Slave 4)',
          start_time: '2026-06-30T10:00:00.000Z',
          end_time: '2026-06-30T12:00:00.000Z',
          power_provided: 2500,
          backup_time: 7200
        },
        {
          battery_name: 'Battery 1 (Slave 3)',
          start_time: '2026-07-01T06:00:00.000Z',
          end_time: '2026-07-01T07:00:00.000Z',
          power_provided: 1800,
          backup_time: 3600
        }
      ]
    };
  }

  const url = `${API_BASE}/api/getLatestSummary?_vehicle=${vehicleDbId}&api_access_token=${authData.token}`;
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  const data = await response.json();
  if (data.status !== 'success') {
    throw new Error(data.message || 'Failed to load latest summary');
  }
  return data.summary;
}
